import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSelection from "components/Records/RecordSelection/GenericRecordSelection";

import AddTextMessagePopup from "./TextMessages/AddTextMessagesPopup";
import TextMessagesRightDrawer from "./TextMessages/TextMessagesRightDrawer";
import TextMessagesBottomDrawer from "./TextMessages/TextMessagesBottomDrawer";
import TextMessagesDetail from "./TextMessages/TextMessagesDetail";
import TextMessageEmptyPage from "./TextMessages/TextMessagesEmptyPage";

import { textMessageAPI } from "api/Interactions/TextMessagesAPI";
import { TextMessage } from "api/Interactions/InteractionsInterfaces";

const TextMessages: React.FC = () => {
  const [messagesData, setTextMessagesData] = useState<TextMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<TextMessage | null>(null);
  const [loading, setLoading] = useState(true);

  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // SAFE ID (null instead of 0)
  const activeId =
    routeId ? Number(routeId) :
    searchParams.get("id") ? Number(searchParams.get("id")) :
    null;

  /** Load list */
  useEffect(() => {
    textMessageAPI
      .getAll()
      .then((data) => setTextMessagesData(data))
      .finally(() => setLoading(false));
  }, []);

  /** Load selected item when URL changes */
  useEffect(() => {
    if (!activeId) {
      setSelectedMessage(null);
      return;
    }

    textMessageAPI
      .getById(activeId)
      .then((detail) => setSelectedMessage(detail))
      .catch(() => setSelectedMessage(null));
  }, [activeId]);

  /** When user clicks on sidebar */
  const handleSelectTextMessage = (id: number) => {
    navigate(`/interactions/text-messages/${id}/`);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <GeneralLayout
      useLeftDrawer
      useRightDrawer
      useBottomDrawer
      defaultOpenLeft={true}
      defaultOpenRight={false}
      defaultOpenBottom={false}
      leftDrawerContent={
        <GenericRecordSelection<TextMessage>
          items={messagesData}
          selectedId={activeId}
          onSelect={(m) => handleSelectTextMessage(m.id)}
          AddPopupComponent={AddTextMessagePopup}
          onCreated={(newMsg) => {
            setTextMessagesData((prev) => [...prev, newMsg]);
            navigate(`/interactions/text-messages/${newMsg.id}/`);
          }}
          idKey="id"
          nameKey={(m) => m.name || `Message ${m.id}`}
          colorKey="interactionsPrimary"
          searchPlaceholder="Search messages..."
        />
      }
      rightDrawerContent={
        activeId ? <TextMessagesRightDrawer textMessageId={activeId} /> : null
      }
      bottomDrawerContent={
        activeId ? <TextMessagesBottomDrawer textMessageId={activeId} /> : null
      }
    >
      {selectedMessage ? (
        <TextMessagesDetail
          messageId={activeId!}
          onClose={() => navigate("/interactions/text-messages/")}
        />
      ) : (
        <TextMessageEmptyPage
          onTextMessagesCreated={(newMsg) => {
            setTextMessagesData((prev) => [...prev, newMsg]);
            navigate(`/interactions/text-messages/${newMsg.id}/`);
          }}
        />
      )}
    </GeneralLayout>
  );
};

export default TextMessages;
